/**
 * Click analytics, sliced every way the data supports.
 *
 * The filters are the §34 set and they apply to everything on the screen at
 * once — the tiles, the chart and all five breakdowns come from one request
 * with one WHERE clause behind it, so a number here can never disagree with
 * the one beside it.
 *
 * Country is shown but will read Unknown until a geo source is configured.
 * That is deliberate: an empty column is a fact, and one filled in by guessing
 * from an IP address would be a measurement nobody could rely on.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking } from '../../lib/urltracking.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { SOURCES, MEDIUMS } from '../../constants/urltracking.js';
import BeaconPanel from './BeaconPanel.jsx';
import { Donut, ShareBar, Columns } from './Charts.jsx';
import {
  ClicksChart, TopList, TrackFilter, TrackState, PresetInput, Recipients, DwellTiles, num
} from './TrackBits.jsx';

export default function TrackAnalytics() {
  const { setSheet } = useApp();
  const [f, setF] = useState({});

  const campaigns = useTracking(() => api.urlTracking.campaigns.list({ limit: 500 }));

  const key = JSON.stringify(f);
  const { data, error, loading } = useTracking(
    () => api.urlTracking.analytics.dashboard(f),
    [key]
  );
  const countries = useTracking(() => api.urlTracking.analytics.by('countries', f), [key]);
  const browsers = useTracking(() => api.urlTracking.analytics.by('browsers', f), [key]);
  const recipients = useTracking(() => api.urlTracking.analytics.recipients(f), [key]);
  const cities = useTracking(() => api.urlTracking.analytics.by('cities', f), [key]);

  const totals = data?.totals || {};
  const nothing = !loading && !error && !totals.clicks;

  return (
    <>
      <TrackFilter value={f} onChange={setF} campaigns={campaigns.data?.data}>
        <div className="f">
          <label htmlFor="uta-source">Source</label>
          <PresetInput
            id="uta-source"
            value={f.source || ''}
            onChange={v => setF({ ...f, source: v })}
            options={SOURCES}
            placeholder="Any"
          />
        </div>
        <div className="f">
          <label htmlFor="uta-medium">Medium</label>
          <PresetInput
            id="uta-medium"
            value={f.medium || ''}
            onChange={v => setF({ ...f, medium: v })}
            options={MEDIUMS}
            placeholder="Any"
          />
        </div>
        <div className="f">
          <label htmlFor="uta-device">Device</label>
          <select id="uta-device" value={f.device || ''} onChange={e => setF({ ...f, device: e.target.value })}>
            <option value="">Any device</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
            <option value="desktop">Desktop</option>
            <option value="bot">Preview crawler</option>
          </select>
        </div>
      </TrackFilter>

      <TrackState
        loading={loading && !data}
        error={error}
        empty={nothing ? (
          <Card>
            <CardBody>
              <EmptyState icon="chart" tone="g-blue" title="No clicks in this selection">
                <p>
                  {Object.values(f).some(Boolean)
                    ? 'Try widening the date range or clearing a filter.'
                    : 'Share a tracking URL and its clicks will show up here within seconds.'}
                </p>
              </EmptyState>
            </CardBody>
          </Card>
        ) : null}
      >
        <TileRow
          tiles={[
            ['Total clicks', num(totals.clicks), 'zap', 'g-purple'],
            ['Unique visitors (est.)', num(totals.uniqueVisitors), 'users', 'g-blue'],
            ['Clicks today', num(totals.clicksToday), 'cal', 'g-green'],
            ['This week', num(totals.clicksThisWeek), 'clock', 'g-cyan'],
            ['This month', num(totals.clicksThisMonth), 'chart', 'g-amber']
          ]}
        />

        <Card>
          <CardHead icon="chart" tone="g-blue" title="Clicks over time">
            <span className="dim">
              {totals.firstClick ? `first click ${new Date(totals.firstClick).toLocaleDateString('en-GB')}` : ''}
            </span>
          </CardHead>
          <CardBody><ClicksChart series={data?.series} /></CardBody>
        </Card>

        <Card>
          <CardHead icon="clock" tone="g-amber" title="Time on the destination" />
          <CardBody><DwellTiles dwell={data?.dwell} /></CardBody>
        </Card>

        {/*
          * Each panel takes the form its question needs.
          *
          * Source and device are part-to-whole with one class usually well
          * ahead, which is what a donut is for. Medium is the same question
          * with two or three classes, where a donut would be a large picture
          * of a small fact, so it gets one line. Content, browser and place
          * are ranked magnitude comparisons with long names — bars answer
          * those better than anything rounder, however much more varied the
          * page would look with six donuts on it.
          */}
        <div className="pair utviz">
          <Card>
            <CardHead icon="target" tone="g-blue" title="Where the clicks come from" />
            <CardBody>
              <Donut rows={data?.sources} title="Clicks by source" />
            </CardBody>
          </Card>
          <Card>
            <CardHead icon="grid" tone="g-cyan" title="What they are reading on" />
            <CardBody>
              <Donut rows={data?.devices} title="Clicks by device" />
            </CardBody>
          </Card>
        </div>

        <div className="utviz">
          <Card>
            <CardHead icon="flow" tone="g-pink" title="How the clicks were earned" />
            <CardBody>
              <ShareBar
                rows={data?.mediums}
                note="The medium is how a link was carried or paid for — marketing, paid, social, referral. Fewer classes than source, so it reads as one line rather than a second circle."
              />
            </CardBody>
          </Card>
        </div>

        <div className="pair utviz">
          <Card>
            <CardHead icon="file" tone="g-orange" title="Which wording worked" />
            <CardBody>
              <Columns
                rows={data?.contents}
                note="Tag two versions of the same message differently and the winner shows up here. Ranked rather than split into slices, because these are usually close and a donut cannot answer which of two similar segments is larger."
                empty="No content tags yet. Set utm_content when you create a link."
              />
            </CardBody>
          </Card>
          <Card>
            <CardHead icon="eye" tone="g-purple" title="Browsers" />
            <CardBody>
              <Columns rows={browsers.data} empty="No clicks yet." />
            </CardBody>
          </Card>
        </div>

        <div className="pair utviz">
          <Card>
            <CardHead icon="building" tone="g-green" title="Countries" />
            <CardBody>
              <Columns
                rows={countries.data}
                note="From an edge header where there is one, or a lookup service when configured. Unknown where neither applies — a guessed location is not one worth reporting."
                empty="No clicks yet."
              />
            </CardBody>
          </Card>
          <Card>
            <CardHead icon="building" tone="g-cyan" title="Cities" />
            <CardBody>
              <Columns
                rows={cities.data}
                note="Cities need a lookup service — an edge header carries the country and nothing finer."
                empty="No clicks yet."
              />
            </CardBody>
          </Card>
        </div>

        <BeaconPanel compact />

        <div className="two">
          <TopList
            title="Top links"
            icon="link"
            tone="g-green"
            rows={data?.topLinks}
            onOpen={l => setSheet({ kind: 'trackLinkView', id: l.id })}
          />
          <TopList
            title="Top campaigns"
            icon="mega"
            tone="g-pink"
            rows={data?.topCampaigns}
            onOpen={c => setSheet({ kind: 'trackCampaignView', id: c.id })}
          />
        </div>

        {/* Rows are not clickable: there is nothing to open. The recipient is
            an account in the clients app, and this module has no business
            opening a client record from an analytics table. */}
        <Recipients data={recipients.data} />

        <Card>
          <CardHead icon="alert" tone="g-amber" title="What these numbers are" />
          <CardBody>
            <ul className="utfacts">
              <li>
                <b>Clicks</b> — somebody followed a tracking link through this server. The firm number.
              </li>
              <li>
                <b>Unique visitors</b> — an estimate. Counted by an anonymous cookie, falling back to a
                hashed address. Clearing cookies or switching device counts again.
              </li>
              <li>
                <b>Not here:</b> messages delivered, messages read, emails opened, conversions. None of
                those can be inferred from a click, and none of them are guessed at.
              </li>
              <li>
                <b>Time on site</b> — reported by the destination itself, so it exists only where the beacon
                is installed. It runs across every page of the visit, and the figure always says how
                many clicks it was measured from.
              </li>
              <li>
                <b>IP and location</b> — the address is recorded and shown to roles that may read
                analytics. It is never put in a URL and never leaves in the aggregate export, which
                groups by country instead.
              </li>
              <li>
                Preview crawlers — the fetch WhatsApp or Slack makes to build a link preview — are
                recorded but excluded from these figures. Filter by device to see them.
              </li>
            </ul>
          </CardBody>
        </Card>
      </TrackState>
    </>
  );
}
